import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import { format, isValid } from "date-fns";
import { BellRing, ChevronLeft, Mail, X } from "lucide-react";
import { ActivityTypes } from "../../constants/action-types";
import Editor from "../../components/Editor";
import ActivityAttendee from "./../ActivityAttendee";
import { useCurrentSize } from "../../CurrentSize";
import CalendarTimeline, { buildTimeValues } from "./../CalendarTimeline";
import InputFieldDebounce from "../../components/ui/InputFieldDebounce";
import { DatePickerField, SelectField } from "../../components/ui";
import InputDebounce from "../../components/InputDebounce";
import SelectItem from "../../main/component/SelectItem";
import { getItem } from "../../api/ProjectApi";

const ActivityForm = ({
  isEditMode,
  appGoogleData,
  appAzureData,
  projectId,
  activityType,
  eventData,
  onDataChange,
  onChangeMentionedMembers,
  validationErrors,
  editorInputDescriptionRef,
  editorInputPrivateDescriptionRef,
}) => {
  const { t } = useTranslation();
  const currentSize = useCurrentSize();

  const [showCalendarTimeline, setShowCalendarTimeline] = useState(true);
  const [toggleCalendarTimeline, setToggleCalendarTimeline] = useState(false);

  const hasGoogleCalendar = appGoogleData.getIn(["data", "googleSetting", "calendar"]) || false;
  const hasGmail =
    (appGoogleData.get("connected") || false) &&
    (appGoogleData.getIn(["data", "googleSetting", "gmail"]) || false);
  const hasOutlook = appAzureData.get("connected") || false;
  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const currentUserEmail = currentUser.get("email");
  const members = useSelector((state: any) => state.projectReducer.getIn(["project", "members"]));

  const timelines = fromJS(buildTimeValues());
  const startTime = new Date(`${eventData.start}T${eventData.startTime}`);

  useEffect(() => {
    if (!isEditMode) {
      const attendees = eventData.attendees || [];
      if (currentUserEmail) {
        if (!attendees.includes(currentUserEmail)) {
          attendees.push(currentUserEmail);
        }
        onDataChange("organizer", currentUserEmail);
      }
      if (eventData.contactId) {
        getItem(eventData.contactId)
          .then((data) => {
            const item = fromJS(data);
            if (data && item.getIn(["data", "email", "value"])) {
              attendees.push(item.getIn(["data", "email", "value"]));
            }
            onDataChange("attendees", attendees);
          })
          .catch(() => {
            onDataChange("attendees", attendees);
          });
      } else {
        onDataChange("attendees", attendees);
      }
    }
  }, [currentUserEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isEditMode) {
      onDataChange("hasGmail", hasGmail);
      onDataChange("hasGoogleCalendar", hasGoogleCalendar);
    }
  }, [hasGmail, hasGoogleCalendar]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isEditMode) {
      onDataChange("hasOutlook", hasOutlook);
    }
  }, [hasOutlook]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1280;
      setShowCalendarTimeline(isDesktop);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getEndTimeOptions = () => {
    if (!eventData.startTime) return [];

    const startTime = eventData.startTime;
    const [startHours, startMinutes] = startTime.split(":").map(Number);
    const startTotalMinutes = startHours * 60 + startMinutes;

    return timelines
      .filter((item) => {
        const timeValue = item.get("value");
        const [hours, minutes] = timeValue.split(":").map(Number);
        const totalMinutes = hours * 60 + minutes;
        return totalMinutes > startTotalMinutes;
      })
      .map((item) => {
        const timeValue = item.get("value");
        const [hours, minutes] = timeValue.split(":").map(Number);
        const totalMinutes = hours * 60 + minutes;
        const durationMinutes = totalMinutes - startTotalMinutes;

        let durationText = "";
        if (durationMinutes < 60) {
          durationText = `(${durationMinutes} min)`;
        } else {
          const hours = Math.floor(durationMinutes / 60);
          const remainingMinutes = durationMinutes % 60;
          if (remainingMinutes === 0) {
            durationText = `(${hours} h)`;
          } else {
            durationText = `(${hours} h ${remainingMinutes} min)`;
          }
        }

        return {
          ...item.toJS(),
          label: `${item.get("label")} ${durationText}`,
          duration: durationMinutes,
        };
      })
      .sort((a, b) => a.duration - b.duration);
  };

  const getTotalTime = () => {
    if (!eventData.startTime || !eventData.endTime) return null;

    const startTime = eventData.startTime;
    const [startHours, startMinutes] = startTime.split(":").map(Number);
    const startTotalMinutes = startHours * 60 + startMinutes;

    const endTime = eventData.endTime;
    const [endHours, endMinutes] = endTime.split(":").map(Number);
    const endTotalMinutes = endHours * 60 + endMinutes;

    let durationText = "";
    const durationMinutes = endTotalMinutes - startTotalMinutes;
    if (durationMinutes < 60) {
      durationText = `(${durationMinutes} minutes)`;
    } else {
      const hours = Math.floor(durationMinutes / 60);
      const remainingMinutes = durationMinutes % 60;
      if (remainingMinutes === 0) {
        durationText = `(${hours} hour)`;
      } else {
        durationText = `(${hours} hour ${remainingMinutes} minutes)`;
      }
    }

    return durationText;
  };

  const handleChangeField = (fieldId, fieldValue) => {
    onDataChange(fieldId, fieldValue);
  };

  const handleSelectSearchItem = (searchItem) => {
    const item = {
      value: searchItem.get("id"),
      label: searchItem.getIn(["data", "name", "value"]),
      type: searchItem.get("type"),
    };
    handleChangeField("item", item);
    if (searchItem.get("type") !== "SALE") {
      const attendeeEmail = searchItem.getIn(["data", "email", "value"]);
      const attendees = [...eventData.attendees];
      if (attendeeEmail && !attendees.includes(attendeeEmail)) {
        attendees.push(attendeeEmail);
      }
      handleChangeField("attendees", attendees);
    } else {
      const contactId =
        searchItem.getIn(["data", "contact", "value", "id"]) ||
        searchItem.getIn(["data", "contact", "value", "value"]);
      handleChangeField("contactId", contactId);
    }
  };

  const getTimePart = (timeString) => {
    const timeData =
      timelines.find((item) => item.get("value") == timeString) || fromJS({ value: "0" });
    return timeData.get("value") == "0" ? undefined : timeData.toJS();
  };

  const renderReminderInput = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div className="flex items-center">
          <div className="flex items-center px-2 space-x-2 h-9 border border-gray-300 bg-gray-100 text-gray-600 rounded-sm rounded-e-none truncate">
            <Mail className="w-4 h-4" />
            <div>{t("Email alert before")}</div>
          </div>
          <div>
            <InputDebounce
              className="max-w-14 h-9 rounded-sm px-3 text-base sm:text-sm/6 placeholder:text-gray-400 border border-gray-300 focus:outline-none border-l-0 border-r-0 rounded-s-none rounded-e-none"
              placeholder={t("min")}
              value={eventData.emailReminderBeforeInMinutes}
              onChange={(value: string) => {
                handleChangeField("emailReminderBeforeInMinutes", value);
              }}
            />
          </div>
          <div className="flex items-center px-2 space-x-2 h-9 border border-gray-300 text-gray-600 rounded-sm border-l-0 rounded-s-none truncate">
            <div>{t("minutes")}</div>
          </div>
        </div>
        <div className="flex items-center">
          <div className="flex items-center px-2 space-x-2 h-9 border border-gray-300 bg-gray-100 text-gray-600 rounded-sm rounded-e-none truncate">
            <BellRing className="w-4 h-4" />
            <div>{t("Web alert before")}</div>
          </div>
          <div>
            <InputDebounce
              className="max-w-14 h-9 rounded-sm px-3 text-base sm:text-sm/6 placeholder:text-gray-400 border border-gray-300 focus:outline-none border-l-0 border-r-0 rounded-s-none rounded-e-none"
              placeholder={t("min")}
              value={eventData.popupReminderBeforeInMinutes}
              onChange={(value: string) => {
                handleChangeField("popupReminderBeforeInMinutes", value);
              }}
            />
          </div>
          <div className="flex items-center px-2 space-x-2 h-9 border border-gray-300 text-gray-600 rounded-sm border-l-0 rounded-s-none truncate">
            <div>{t("minutes")}</div>
          </div>
        </div>
      </div>
    );
  };

  const renderForm = () => {
    return (
      <>
        <div>
          <div className="w-full flex items-center justify-between">
            <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Title")}</label>
            {!showCalendarTimeline ? (
              <div>
                <div
                  className="mb-2 w-6 h-6 flex items-center justify-center bg-white border border-gray-200 rounded-full shadow-xl hover:shadow-2xl transition-all duration-200 cursor-pointer"
                  onClick={() => setToggleCalendarTimeline(true)}
                >
                  <ChevronLeft className="w-4 h-4 text-gray-600 transition-colors duration-200" />
                </div>
                <aside
                  className={`absolute z-9999 top-0 right-0 h-full min-w-80 bg-white rounded-xl shadow-lg transform transition-transform duration-300 ${
                    toggleCalendarTimeline ? "translate-x-0" : "hidden translate-x-full"
                  }`}
                >
                  <div className="p-4 w-full flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-800">
                      {isValid(startTime)
                        ? format(startTime, "EEEE, MMMM dd, yyyy")
                        : t("Select Date")}
                    </h3>
                    <div>
                      <X
                        className="w-4 h-4 text-gray-600"
                        onClick={() => setToggleCalendarTimeline(false)}
                      />
                    </div>
                  </div>
                  <CalendarTimeline
                    itemId={eventData.item.value}
                    anchorItemType={eventData.item?.type}
                    eventData={eventData}
                    height={currentSize.height - 300}
                  />
                </aside>
              </div>
            ) : null}
          </div>
          <InputFieldDebounce
            placeholder={t("Type...")}
            value={eventData.summary}
            onChange={(value) => handleChangeField("summary", value)}
            errorMessage={validationErrors.summary}
          />
        </div>
        <div>
          <div className="w-full">
            <label className="block text-sm font-semibold text-gray-800 mb-2">
              {t("Description")}
            </label>
            <div className="max-w-2xl rounded-lg border border-gray-300 [&_.ql-container]:!border-0">
              <Editor
                editorInputRef={editorInputDescriptionRef}
                showToolbar={false}
                minRows={4}
                members={members}
                editorContent={{
                  htmlContent: eventData.description,
                  contents: {},
                }}
                onChange={(content) => {
                  onChangeMentionedMembers(content.mentionedMembers);
                  handleChangeField("description", content.htmlContent);
                }}
              />
            </div>
          </div>
        </div>
        <div>
          <div className="w-full">
            <label className="block text-sm font-semibold text-gray-800 mb-2">
              {t("Date & Time")}
            </label>
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 w-full">
              <div className={"w-full sm:w-36"}>
                <DatePickerField
                  value={eventData.start}
                  onChange={(value) => {
                    handleChangeField("start", value);
                    handleChangeField("end", value);
                  }}
                  disabledSelectDate={{ before: new Date() }}
                />
              </div>
              <div className={"w-full sm:w-36"}>
                <SelectField
                  value={getTimePart(eventData.startTime)}
                  onChange={(selectedDate) => {
                    handleChangeField("startTime", selectedDate.value);
                    if (activityType === ActivityTypes.deadline) {
                      handleChangeField("endTime", selectedDate.value);
                    }
                  }}
                  options={timelines.toJS()}
                  placeholder={t("Start")}
                  isOptionSelected={(option, selectValue) => {
                    return selectValue && selectValue[0] && option.value == selectValue[0].value;
                  }}
                  filterOption={(option, inputValue) => {
                    if (!inputValue) return true;
                    return option.label.toLowerCase().includes(inputValue.toLowerCase());
                  }}
                  stylesOverride={{
                    menu: (provided) => ({
                      ...provided,
                      minWidth: "190px",
                      width: "auto",
                    }),
                    option: (provided) => ({
                      ...provided,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }),
                  }}
                />
              </div>
              {activityType !== ActivityTypes.deadline ? (
                <>
                  <div className={"w-full sm:w-36"}>
                    <SelectField
                      value={getTimePart(eventData.endTime)}
                      onChange={(selectedDate) => {
                        handleChangeField("endTime", selectedDate.value);
                      }}
                      options={getEndTimeOptions()}
                      placeholder={t("End")}
                      isDisabled={!eventData.startTime}
                      stylesOverride={{
                        menu: (provided) => ({
                          ...provided,
                          minWidth: "200px",
                          width: "auto",
                        }),
                        option: (provided) => ({
                          ...provided,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }),
                      }}
                    />
                  </div>
                  <div className={"w-full text-gray-600 sm:w-36"}>{getTotalTime()}</div>
                </>
              ) : null}
            </div>
            {validationErrors.startTimeEndTime && (
              <p className="text-xs text-red-600 mt-1">{validationErrors.startTimeEndTime}</p>
            )}
          </div>
        </div>
        <div>
          {hasGoogleCalendar ? (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
              <div className="flex items-center gap-x-2">
                <input
                  id="comments"
                  name="comments"
                  type="checkbox"
                  checked={eventData.reminderEnable}
                  onChange={(value) => {
                    handleChangeField("reminderEnable", value.target.checked);
                  }}
                  className="h-4 w-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-600"
                />
                <label htmlFor="comments" className="text-sm font-medium text-gray-900">
                  {t("Reminder")}
                </label>
              </div>
              <div className="md:col-span-4">
                {eventData.reminderEnable ? (
                  renderReminderInput()
                ) : (
                  <div className="text-gray-500">
                    {t("Get notified when an event is happening.")}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
        <div>
          <ActivityAttendee
            title={t("Participants")}
            addButtonText={t("Add Participant")}
            projectId={projectId}
            eventData={eventData}
            handleChange={handleChangeField}
            isOrganizer
          />
          {validationErrors.attendees && (
            <p className="text-xs text-red-600 mt-1">{validationErrors.attendees}</p>
          )}
        </div>
        <div>
          <div className="w-full">
            <label className="block text-sm font-semibold text-gray-800 mb-2">
              {t("Linked Item")}
            </label>
            <div className="flex items-center">
              <SelectItem
                selectedItem={eventData.item}
                onSelectItem={(searchItem) => {
                  if (searchItem.get("id")) {
                    handleSelectSearchItem(searchItem);
                  } else {
                    handleChangeField("item", {
                      type: searchItem.get("type"),
                    });
                  }
                }}
              />
            </div>
            {validationErrors.linkItem && (
              <p className="text-xs text-red-600 mt-1">{validationErrors.linkItem}</p>
            )}
          </div>
        </div>
        <div>
          <InputFieldDebounce
            label={t("Location")}
            placeholder={t("Type...")}
            value={eventData.location}
            onChange={(value) => handleChangeField("location", value)}
          />
        </div>
        <div>
          <div className="w-full">
            <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Notes")}</label>
            <div className="max-w-2xl rounded-lg border border-gray-300 [&_.ql-container]:!border-0">
              <Editor
                editorInputRef={editorInputPrivateDescriptionRef}
                showToolbar={false}
                members={members}
                editorContent={{
                  htmlContent: eventData.privateDescription,
                  contents: {},
                }}
                onChange={(value) => {
                  handleChangeField("privateDescription", value.htmlContent);
                }}
                isBorderRadius={true}
                placeholder={t("For internal usage only, not visible for external participants")}
              />
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderCalendarTimeline = () => {
    if (!showCalendarTimeline) return null;
    return (
      <div>
        <h3 className="text-sm font-semibold text-gray-800">
          {isValid(startTime) ? format(startTime, "EEEE, MMMM dd, yyyy") : t("Select Date")}
        </h3>
        <CalendarTimeline
          itemId={eventData.item.value}
          anchorItemType={eventData.item?.type}
          eventData={eventData}
          height={currentSize.height - 300}
        />
      </div>
    );
  };

  return (
    <div className="min-w-md grid grid-cols-3 gap-2">
      <div
        className={`col-span-${showCalendarTimeline ? 2 : 3} space-y-4`}
        style={{ height: currentSize.height - 300 }}
      >
        {renderForm()}
      </div>
      {renderCalendarTimeline()}
    </div>
  );
};

export default ActivityForm;
